import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Sparkles } from 'lucide-react';

export default function ArtikelMinimalistischeEidDeko() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"><ArrowLeft className="w-4 h-4" />Alle Ratgeber</Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4"><Calendar className="w-4 h-4" /><span>Februar 2026</span></div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">Minimalistische Eid Dekoration: Weniger ist mehr</h1>
          <p className="text-lg text-white/80 leading-relaxed">Elegante, zurückhaltende Deko-Ideen für ein stilvolles Eid-Fest.</p>
        </div>
      </section>
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 prose prose-lg max-w-none">
          <p className="text-xl text-[#2C332E] leading-relaxed mb-8">Nicht jeder mag opulente Dekoration – und das ist völlig in Ordnung! Minimalistische Eid-Deko ist elegant, zeitlos und lässt Raum für das Wesentliche: Familie, Gemeinschaft und Spiritualität.</p>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Grundprinzipien minimalistischer Deko</h2>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">1. Wenige, aber hochwertige Stücke</h4>
                <p className="text-sm text-[#4B5563]">Statt viele kleine Deko-Elemente lieber ein oder zwei Statement-Pieces.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">2. Natürliche Materialien</h4>
                <p className="text-sm text-[#4B5563]">Holz, Keramik, Leinen, Glas – zeitlos und elegant.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">3. Neutrale Farbpalette</h4>
                <p className="text-sm text-[#4B5563]">Weiß, Creme, Beige, Salbeigrün – mit einem Hauch Gold als Akzent.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">4. Funktionale Schönheit</h4>
                <p className="text-sm text-[#4B5563]">Deko, die auch einen Zweck erfüllt: Kerzen, Vasen mit Blumen, schöne Schalen für Datteln.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center"><Sparkles className="w-6 h-6 text-[#4A5D4F]" /></div>
              <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Minimalistische Deko-Ideen</h2>
            </div>
            <div className="space-y-6">
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🌿 Einzelne Vase mit Zweigen</h4>
                <p className="text-sm text-[#4B5563]">Eine schlichte Keramikvase mit Eukalyptus- oder Olivenzweigen. Optional: Ein goldener Mond-Anhänger an einem Zweig.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🕯️ Drei Kerzen in einer Reihe</h4>
                <p className="text-sm text-[#4B5563]">Weiße oder cremefarbene Stumpenkerzen in unterschiedlichen Höhen. Schlicht und atmosphärisch.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🌙 Ein einzelner Holz-Mond</h4>
                <p className="text-sm text-[#4B5563]">Ein minimalistischer Holzmond auf einem Regal oder der Fensterbank. Kein Glitzer, keine Farbe – nur die Form.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">📜 Kalligraphie-Druck</h4>
                <p className="text-sm text-[#4B5563]">Ein gerahmter Print mit "Eid Mubarak" oder einem Koranvers in schlichtem Schwarz-Weiß.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🍽️ Natürlich gedeckter Tisch</h4>
                <p className="text-sm text-[#4B5563]">Leinenservietten, ein paar frische Blumen, eine Schale mit Datteln. Keine Girlanden, keine Ballons – nur Eleganz.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Farbpalette</h2>
            <div className="grid grid-cols-5 gap-3">
              <div className="text-center">
                <div className="w-full aspect-square rounded-lg bg-white border border-gray-200 mb-2"></div>
                <span className="text-xs text-[#4B5563]">Weiß</span>
              </div>
              <div className="text-center">
                <div className="w-full aspect-square rounded-lg bg-[#F5F5F0] mb-2"></div>
                <span className="text-xs text-[#4B5563]">Creme</span>
              </div>
              <div className="text-center">
                <div className="w-full aspect-square rounded-lg bg-[#E8D5C4] mb-2"></div>
                <span className="text-xs text-[#4B5563]">Beige</span>
              </div>
              <div className="text-center">
                <div className="w-full aspect-square rounded-lg bg-[#9CAF88] mb-2"></div>
                <span className="text-xs text-[#4B5563]">Salbei</span>
              </div>
              <div className="text-center">
                <div className="w-full aspect-square rounded-lg bg-[#C9A962] mb-2"></div>
                <span className="text-xs text-[#4B5563]">Gold</span>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Was du weglassen kannst</h2>
            <div className="bg-[#4A5D4F]/10 rounded-xl p-6">
              <ul className="text-[#4B5563] space-y-2">
                <li>✗ Luftballons und Folienballons</li>
                <li>✗ Glitzer und Pailletten</li>
                <li>✗ Bunte Lichterketten</li>
                <li>✗ Viele kleine Deko-Elemente</li>
                <li>✗ Plastik-Dekoration</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Vorteile minimalistischer Deko</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Weniger Aufwand</h4>
                <p className="text-sm text-[#4B5563]">Schnell auf- und abgebaut, mehr Zeit für die Familie.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Zeitlos</h4>
                <p className="text-sm text-[#4B5563]">Jedes Jahr wiederverwendbar, nie out-of-style.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Nachhaltiger</h4>
                <p className="text-sm text-[#4B5563]">Kein Einweg-Plastik, weniger Müll.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Fokus auf Wesentliches</h4>
                <p className="text-sm text-[#4B5563]">Die Spiritualität steht im Vordergrund, nicht die Deko.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Fazit</h2>
            <p className="text-[#4B5563] leading-relaxed mb-4">Minimalistische Eid-Dekoration ist für alle, die Eleganz über Opulenz stellen. Ein paar ausgewählte Stücke können genauso festlich wirken wie eine Explosion aus Glitzer – nur eben auf eine ruhigere, zeitlosere Art.</p>
            <p className="text-xl text-[#4A5D4F] font-medium">Eid Mubarak! 🌙</p>
          </section>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-8">
            <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-4">Weitere Artikel</h3>
            <ul className="space-y-2">
              <li><Link to="/ratgeber/eid-deko-ideen" className="text-[#4A5D4F] hover:underline">→ Eid Deko Ideen für Zuhause</Link></li>
              <li><Link to="/ratgeber/moderne-muslimische-wohnideen" className="text-[#4A5D4F] hover:underline">→ Moderne muslimische Wohnideen</Link></li>
            </ul>
          </div>

          <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl font-medium text-white mb-4">Geschenke entdecken</h3>
            <Link to="/geschenke" className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors">Jetzt stöbern<Gift className="w-5 h-5" /></Link>
          </div>
        </div>
      </article>
    </main>
  );
}
